import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import sanitizeText from '@/utils/sanitizeText';
import {
  FaShieldAlt,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSync,
  FaCheckCircle,
  FaExclamationTriangle,
  FaToggleOn,
  FaToggleOff,
  FaSave,
  FaTimes,
  FaGlobe,
  FaServer,
  FaClock,
  FaGripVertical,
  FaExchangeAlt,
  FaListUl,
  FaLayerGroup,
  FaTachometerAlt,
  FaRoute,
  FaNetworkWired,
  FaDesktop,
  FaCode
} from 'react-icons/fa';
import './RouterFirewallEditor.scss';
import MultiRouterDeployment from './MultiRouterDeployment';
import * as Sentry from '@sentry/react';

/**
 * RouterFirewallEditor Component
 *
 * Comprehensive MikroTik router management combining per-device and global configuration:
 * - Filter Rules, NAT Rules, Address Lists (firewall)
 * - Mangle Rules (packet marking / QoS tagging)
 * - Simple Queues (bandwidth management)
 * - Static Routes
 * - Static DNS entries
 * - DHCP Leases
 *
 * Features:
 * - Inline editing with immediate RouterOS command execution
 * - Global vs Local badge indicators on all rule types
 * - Enable/disable toggle
 * - Drag-and-drop reordering (RouterOS move command)
 * - Add / Edit / Delete modals
 */
const RouterFirewallEditor = ({ routerId, routerName }) => {
  const { apiFetch } = useAuth();

  const [mode, setMode] = useState('single');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Data
  const [filterRules, setFilterRules] = useState([]);
  const [natRules, setNatRules] = useState([]);
  const [addressLists, setAddressLists] = useState([]);
  const [mangleRules, setMangleRules] = useState([]);
  const [queues, setQueues] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [dnsEntries, setDnsEntries] = useState([]);
  const [dhcpLeases, setDhcpLeases] = useState([]);
  const [l7Protocols, setL7Protocols] = useState([]);
  const [lastFetched, setLastFetched] = useState(null);

  // Tab
  const [activeTab, setActiveTab] = useState('filter');

  // Edit
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Add modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addModalType, setAddModalType] = useState('filter');
  const [newRuleForm, setNewRuleForm] = useState({});

  // Drag-and-drop
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragItemRef = useRef(null);

  // ===== Default Forms =====
  const defaultForms = {
    filter: { chain: 'forward', action: 'accept', protocol: 'tcp', srcAddress: '', dstAddress: '', srcAddressList: '', dstAddressList: '', srcPort: '', dstPort: '', comment: '', inInterface: '', outInterface: '', connectionState: '', log: false },
    nat: { chain: 'dstnat', action: 'dst-nat', protocol: 'tcp', srcAddress: '', dstAddress: '', srcAddressList: '', dstAddressList: '', srcPort: '', dstPort: '', toAddresses: '', toPorts: '', comment: '', inInterface: '', log: false },
    addresslist: { list: '', address: '', comment: '', timeout: '' },
    mangle: { chain: 'prerouting', action: 'mark-connection', newConnectionMark: '', newPacketMark: '', newRoutingMark: '', protocol: '', srcAddress: '', dstAddress: '', srcAddressList: '', dstAddressList: '', srcPort: '', dstPort: '', comment: '', passthrough: true },
    queue: { name: '', target: '', maxLimit: '', burstLimit: '', burstThreshold: '', burstTime: '', comment: '', parent: 'none', priority: '8', disabled: false },
    route: { dstAddress: '0.0.0.0/0', gateway: '', distance: '1', routingMark: '', comment: '' },
    dns: { name: '', address: '', ttl: '1d', comment: '' },
    dhcp: { address: '', macAddress: '', server: 'defconf', comment: '' },
    l7: { name: '', regexp: '', comment: '' }
  };

  // ===== Validation =====
  const escapeRouterOSValue = (value) => {
    if (!value) return '';
    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  };

  const isValidRuleId = (id) => {
    if (!id) return false;
    return /^\*[0-9a-fA-F]+$/.test(id) || /^[a-zA-Z0-9]+$/.test(id);
  };

  const isValidIPAddressOrCIDR = (value) => {
    if (!value || value === '' || value === 'any') return true;
    // Allow hostnames/FQDNs (MikroTik address lists support them)
    if (/^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*$/.test(value)) return true;
    const parts = value.split('/');
    const ip = parts[0];
    const octets = ip.split('.');
    if (octets.length !== 4) return false;
    for (const octet of octets) {
      const num = parseInt(octet, 10);
      if (isNaN(num) || num < 0 || num > 255) return false;
    }
    if (parts.length === 2) {
      const cidr = parseInt(parts[1], 10);
      if (isNaN(cidr) || cidr < 0 || cidr > 32) return false;
    }
    return true;
  };

  const isValidPort = (value) => {
    if (!value || value === '' || value === 'any') return true;
    if (value.includes('-')) {
      const [s, e] = value.split('-').map(v => parseInt(v, 10));
      return !isNaN(s) && !isNaN(e) && s >= 1 && s <= 65535 && e >= 1 && e <= 65535 && s < e;
    }
    const port = parseInt(value, 10);
    return !isNaN(port) && port >= 1 && port <= 65535;
  };

  const isValidProtocol = (value) => {
    if (!value || value === '') return true;
    return ['tcp', 'udp', 'icmp', 'icmpv6', 'gre', 'esp', 'ah', 'ipencap', 'ospf'].includes(value.toLowerCase());
  };

  const isValidChain = (value, type = 'filter') => {
    if (type === 'nat') return ['srcnat', 'dstnat'].includes(value?.toLowerCase());
    if (type === 'mangle') return ['prerouting', 'input', 'forward', 'output', 'postrouting'].includes(value?.toLowerCase());
    return ['input', 'forward', 'output'].includes(value?.toLowerCase());
  };

  const isValidAction = (value, type = 'filter') => {
    if (type === 'nat') return ['src-nat', 'dst-nat', 'masquerade', 'netmap', 'redirect', 'accept', 'drop', 'passthrough', 'return', 'jump'].includes(value?.toLowerCase());
    if (type === 'mangle') return ['mark-connection', 'mark-packet', 'mark-routing', 'accept', 'drop', 'passthrough', 'return', 'jump', 'change-mss', 'change-dscp', 'change-ttl'].includes(value?.toLowerCase());
    return ['accept', 'drop', 'reject', 'tarpit', 'passthrough', 'return', 'jump'].includes(value?.toLowerCase());
  };

  const isValidConnectionState = (value) => {
    if (!value || value === '') return true;
    const states = value.split(',').map(s => s.trim().toLowerCase());
    return states.every(state => ['established', 'related', 'new', 'invalid', 'untracked'].includes(state));
  };

  const isValidMacAddress = (value) => {
    if (!value || value === '') return false;
    return /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/.test(value.trim());
  };

  // ===== Data Loading =====
  const loadFirewallRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/api/RouterConfig/${routerId}/firewall/live-rules`);
      const data = await response.json();
      if (data.success) {
        setFilterRules(data.data?.filterRules || []);
        setNatRules(data.data?.natRules || []);
        setAddressLists(data.data?.addressList || []);
        setMangleRules(data.data?.mangleRules || []);
        setQueues(data.data?.queues || []);
        setRoutes(data.data?.routes || []);
        setDnsEntries(data.data?.dnsEntries || []);
        setDhcpLeases(data.data?.dhcpLeases || []);
        setL7Protocols(data.data?.layer7Protocols || []);
        setLastFetched(data.data?.fetchedAt || new Date().toISOString());
      } else {
        setError(data.error || 'Failed to load rules');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error loading rules:', err);
      setError(err.message || 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, routerId]);

  useEffect(() => {
    (async () => { await loadFirewallRules(); })();
  }, [loadFirewallRules]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // ===== Execute Command Helper =====
  const executeCommand = async (command) => {
    const response = await apiFetch(`/api/RouterConfig/${routerId}/execute-command`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ command })
    });
    return response.json();
  };

  // ===== Editing =====
  const startEditingRule = (rule) => { setEditingRuleId(rule.id); setEditForm({ ...rule }); };
  const cancelEditing = () => { setEditingRuleId(null); setEditForm({}); };

  const saveEditedRule = async (ruleType = 'filter') => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const command = buildUpdateCommand(editForm, ruleType);
      const data = await executeCommand(command);
      if (data.success) {
        setSuccess('Rule updated successfully');
        cancelEditing();
        await loadFirewallRules();
      } else {
        setError(data.error || 'Failed to update rule');
      }
    } catch (err) {
        Sentry.captureException(err);
      setError(err.message || 'Failed to update rule');
    } finally {
      setSaving(false);
    }
  };

  const toggleRuleState = async (rule, ruleType = 'filter') => {
    if (!isValidRuleId(rule.id)) { setError('Invalid rule ID'); return; }
    setSaving(true); setError(null);
    try {
      const newState = !rule.disabled;
      const escapedId = escapeRouterOSValue(rule.id);
      const pathMap = { nat: 'nat', addresslist: 'address-list', mangle: 'mangle', queue: 'queue simple', route: 'route', dns: 'dns static', dhcp: 'dhcp-server lease', l7: 'layer7-protocol' };
      const path = pathMap[ruleType] || 'filter';
      const prefix = ['queue', 'route', 'dns', 'dhcp'].includes(ruleType) ? '/ip' : '/ip firewall';
      const command = `${prefix} ${path} set [find .id="${escapedId}"] disabled=${newState ? 'yes' : 'no'}`;
      const data = await executeCommand(command);
      if (data.success) {
        setSuccess(`Rule ${newState ? 'disabled' : 'enabled'} successfully`);
        await loadFirewallRules();
      } else {
        setError(data.error || 'Failed to toggle rule');
      }
    } catch (err) {
        Sentry.captureException(err);
      setError(err.message || 'Failed to toggle rule');
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async (rule, ruleType = 'filter') => {
    if (!isValidRuleId(rule.id)) { setError('Invalid rule ID'); return; }
    const label = rule.comment || rule.name || rule.list || 'No comment';
    if (!window.confirm(`Delete this ${ruleType} entry?\n\n${label}`)) return;
    setSaving(true); setError(null);
    try {
      const escapedId = escapeRouterOSValue(rule.id);
      const pathMap = { nat: 'nat', addresslist: 'address-list', mangle: 'mangle', queue: 'queue simple', route: 'route', dns: 'dns static', dhcp: 'dhcp-server lease', l7: 'layer7-protocol' };
      const path = pathMap[ruleType] || 'filter';
      const prefix = ['queue', 'route', 'dns', 'dhcp'].includes(ruleType) ? '/ip' : '/ip firewall';
      const command = `${prefix} ${path} remove [find .id="${escapedId}"]`;
      const data = await executeCommand(command);
      if (data.success) {
        setSuccess('Entry deleted successfully');
        await loadFirewallRules();
      } else {
        setError(data.error || 'Failed to delete');
      }
    } catch (err) {
        Sentry.captureException(err);
      setError(err.message || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  // ===== Add Rule =====
  const openAddModal = (type) => {
    setAddModalType(type);
    setNewRuleForm({ ...(defaultForms[type] || {}) });
    setShowAddModal(true);
  };

  const addNewRule = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const command = buildAddCommand(newRuleForm, addModalType);
      const data = await executeCommand(command);
      if (data.success) {
        setSuccess('Entry added successfully');
        setShowAddModal(false);
        await loadFirewallRules();
      } else {
        setError(data.error || 'Failed to add');
      }
    } catch (err) {
        Sentry.captureException(err);
      setError(err.message || 'Failed to add');
    } finally {
      setSaving(false);
    }
  };

  // ===== Command Builders =====
  const buildAddCommand = (form, type = 'filter') => {
    if (type === 'addresslist') {
      const listName = form.list === '__custom__' ? form.customList : form.list;
      if (!listName) throw new Error('List name is required');
      if (!form.address || !isValidIPAddressOrCIDR(form.address)) throw new Error('Valid address required');
      let cmd = `/ip firewall address-list add list=${escapeRouterOSValue(listName)} address=${form.address}`;
      if (form.timeout) cmd += ` timeout=${escapeRouterOSValue(form.timeout)}`;
      if (form.comment) cmd += ` comment="${escapeRouterOSValue(form.comment)}"`;
      return cmd;
    }
    if (type === 'queue') {
      if (!form.name) throw new Error('Queue name is required');
      if (!form.target) throw new Error('Target is required');
      let cmd = `/ip queue simple add name="${escapeRouterOSValue(form.name)}" target=${form.target}`;
      if (form.maxLimit) cmd += ` max-limit=${form.maxLimit}`;
      if (form.burstLimit) cmd += ` burst-limit=${form.burstLimit}`;
      if (form.burstThreshold) cmd += ` burst-threshold=${form.burstThreshold}`;
      if (form.burstTime) cmd += ` burst-time=${form.burstTime}`;
      if (form.priority && form.priority !== '8') cmd += ` priority=${form.priority}`;
      if (form.parent && form.parent !== 'none') cmd += ` parent=${escapeRouterOSValue(form.parent)}`;
      if (form.comment) cmd += ` comment="${escapeRouterOSValue(form.comment)}"`;
      return cmd;
    }
    if (type === 'route') {
      if (!form.gateway) throw new Error('Gateway is required');
      let cmd = `/ip route add dst-address=${form.dstAddress || '0.0.0.0/0'} gateway=${form.gateway}`;
      if (form.distance) cmd += ` distance=${form.distance}`;
      if (form.routingMark) cmd += ` routing-mark=${escapeRouterOSValue(form.routingMark)}`;
      if (form.comment) cmd += ` comment="${escapeRouterOSValue(form.comment)}"`;
      return cmd;
    }
    if (type === 'dns') {
      if (!form.name || !form.address) throw new Error('Name and address are required');
      let cmd = `/ip dns static add name=${escapeRouterOSValue(form.name)} address=${form.address}`;
      if (form.ttl) cmd += ` ttl=${form.ttl}`;
      if (form.comment) cmd += ` comment="${escapeRouterOSValue(form.comment)}"`;
      return cmd;
    }
    if (type === 'dhcp') {
      if (!form.address || !form.macAddress) throw new Error('Address and MAC required');
      if (!isValidMacAddress(form.macAddress)) throw new Error('Invalid MAC address format (expected AA:BB:CC:DD:EE:FF)');
      let cmd = `/ip dhcp-server lease add address=${form.address} mac-address=${form.macAddress}`;
      if (form.server) cmd += ` server=${escapeRouterOSValue(form.server)}`;
      if (form.comment) cmd += ` comment="${escapeRouterOSValue(form.comment)}"`;
      return cmd;
    }
    if (type === 'l7') {
      if (!form.name || !form.name.trim()) throw new Error('Protocol name is required');
      if (!form.regexp || !form.regexp.trim()) throw new Error('Regex pattern is required');
      // RouterOS layer7-protocol names may not contain spaces
      const safeName = form.name.trim().replace(/\s+/g, '-');
      let cmd = `/ip firewall layer7-protocol add name=${escapeRouterOSValue(safeName)} regexp="${escapeRouterOSValue(form.regexp.trim())}"`;
      if (form.comment) cmd += ` comment="${escapeRouterOSValue(form.comment)}"`;
      return cmd;
    }

    // filter, nat, mangle
    const path = type === 'nat' ? 'nat' : type === 'mangle' ? 'mangle' : 'filter';
    if (!isValidChain(form.chain, type)) throw new Error('Invalid chain');
    if (!isValidAction(form.action, type)) throw new Error('Invalid action');
    if (form.protocol && !isValidProtocol(form.protocol)) throw new Error('Invalid protocol');
    if (!isValidIPAddressOrCIDR(form.srcAddress)) throw new Error('Invalid source address');
    if (!isValidIPAddressOrCIDR(form.dstAddress)) throw new Error('Invalid destination address');
    if (!isValidPort(form.srcPort)) throw new Error('Invalid source port');
    if (!isValidPort(form.dstPort)) throw new Error('Invalid destination port');
    if (form.connectionState && !isValidConnectionState(form.connectionState)) throw new Error('Invalid connection state');

    let cmd = `/ip firewall ${path} add chain=${form.chain} action=${form.action}`;
    if (form.protocol) cmd += ` protocol=${form.protocol}`;
    if (form.srcAddress) cmd += ` src-address=${form.srcAddress}`;
    if (form.dstAddress) cmd += ` dst-address=${form.dstAddress}`;
    if (form.srcAddressList) cmd += ` src-address-list=${escapeRouterOSValue(form.srcAddressList)}`;
    if (form.dstAddressList) cmd += ` dst-address-list=${escapeRouterOSValue(form.dstAddressList)}`;
    if (form.srcPort) cmd += ` src-port=${form.srcPort}`;
    if (form.dstPort) cmd += ` dst-port=${form.dstPort}`;
    if (form.inInterface) cmd += ` in-interface=${escapeRouterOSValue(form.inInterface)}`;
    if (form.outInterface) cmd += ` out-interface=${escapeRouterOSValue(form.outInterface)}`;
    if (form.connectionState) cmd += ` connection-state=${form.connectionState}`;
    if (form.toAddresses) cmd += ` to-addresses=${form.toAddresses}`;
    if (form.toPorts) cmd += ` to-ports=${form.toPorts}`;
    if (form.newConnectionMark) cmd += ` new-connection-mark=${escapeRouterOSValue(form.newConnectionMark)}`;
    if (form.newPacketMark) cmd += ` new-packet-mark=${escapeRouterOSValue(form.newPacketMark)}`;
    if (form.newRoutingMark) cmd += ` new-routing-mark=${escapeRouterOSValue(form.newRoutingMark)}`;
    if (form.passthrough === false) cmd += ` passthrough=no`;
    if (form.comment) cmd += ` comment="${escapeRouterOSValue(form.comment)}"`;
    if (form.log) cmd += ` log=yes`;
    return cmd;
  };

  const buildUpdateCommand = (form, type = 'filter') => {
    if (!isValidRuleId(form.id)) throw new Error('Invalid rule ID');
    const escapedId = escapeRouterOSValue(form.id);

    if (type === 'addresslist') {
      let cmd = `/ip firewall address-list set [find .id="${escapedId}"]`;
      if (form.list !== undefined) cmd += ` list=${escapeRouterOSValue(form.list)}`;
      if (form.address !== undefined) cmd += ` address=${form.address}`;
      if (form.comment !== undefined) cmd += ` comment="${escapeRouterOSValue(form.comment || '')}"`;
      if (form.timeout !== undefined && form.timeout) cmd += ` timeout=${escapeRouterOSValue(form.timeout)}`;
      return cmd;
    }
    if (type === 'queue') {
      let cmd = `/ip queue simple set [find .id="${escapedId}"]`;
      if (form.name) cmd += ` name="${escapeRouterOSValue(form.name)}"`;
      if (form.target) cmd += ` target=${form.target}`;
      if (form.maxLimit !== undefined) cmd += ` max-limit=${form.maxLimit || '0/0'}`;
      if (form.comment !== undefined) cmd += ` comment="${escapeRouterOSValue(form.comment || '')}"`;
      return cmd;
    }
    if (type === 'route') {
      let cmd = `/ip route set [find .id="${escapedId}"]`;
      if (form.gateway) cmd += ` gateway=${form.gateway}`;
      if (form.distance) cmd += ` distance=${form.distance}`;
      if (form.comment !== undefined) cmd += ` comment="${escapeRouterOSValue(form.comment || '')}"`;
      return cmd;
    }
    if (type === 'dns') {
      let cmd = `/ip dns static set [find .id="${escapedId}"]`;
      if (form.name) cmd += ` name=${escapeRouterOSValue(form.name)}`;
      if (form.address) cmd += ` address=${form.address}`;
      if (form.comment !== undefined) cmd += ` comment="${escapeRouterOSValue(form.comment || '')}"`;
      return cmd;
    }
    if (type === 'dhcp') {
      let cmd = `/ip dhcp-server lease set [find .id="${escapedId}"]`;
      if (form.address) cmd += ` address=${form.address}`;
      if (form.macAddress) cmd += ` mac-address=${form.macAddress}`;
      if (form.comment !== undefined) cmd += ` comment="${escapeRouterOSValue(form.comment || '')}"`;
      return cmd;
    }
    if (type === 'l7') {
      let cmd = `/ip firewall layer7-protocol set [find .id="${escapedId}"]`;
      if (form.name) {
        const safeName = form.name.trim().replace(/\s+/g, '-');
        cmd += ` name=${escapeRouterOSValue(safeName)}`;
      }
      if (form.regexp !== undefined) cmd += ` regexp="${escapeRouterOSValue(form.regexp || '')}"`;
      if (form.comment !== undefined) cmd += ` comment="${escapeRouterOSValue(form.comment || '')}"`;
      return cmd;
    }

    const path = type === 'nat' ? 'nat' : type === 'mangle' ? 'mangle' : 'filter';
    if (form.chain && !isValidChain(form.chain, type)) throw new Error('Invalid chain');
    if (form.action && !isValidAction(form.action, type)) throw new Error('Invalid action');
    if (form.protocol && !isValidProtocol(form.protocol)) throw new Error('Invalid protocol');
    if (form.srcAddress !== undefined && !isValidIPAddressOrCIDR(form.srcAddress)) throw new Error('Invalid source address');
    if (form.dstAddress !== undefined && !isValidIPAddressOrCIDR(form.dstAddress)) throw new Error('Invalid destination address');

    let cmd = `/ip firewall ${path} set [find .id="${escapedId}"]`;
    if (form.chain) cmd += ` chain=${form.chain}`;
    if (form.action) cmd += ` action=${form.action}`;
    if (form.protocol) cmd += ` protocol=${form.protocol}`;
    if (form.srcAddress !== undefined) cmd += ` src-address=${form.srcAddress || ''}`;
    if (form.dstAddress !== undefined) cmd += ` dst-address=${form.dstAddress || ''}`;
    if (form.srcAddressList !== undefined) cmd += ` src-address-list=${escapeRouterOSValue(form.srcAddressList || '')}`;
    if (form.dstAddressList !== undefined) cmd += ` dst-address-list=${escapeRouterOSValue(form.dstAddressList || '')}`;
    if (form.srcPort !== undefined) cmd += ` src-port=${form.srcPort || ''}`;
    if (form.dstPort !== undefined) cmd += ` dst-port=${form.dstPort || ''}`;
    if (form.toAddresses !== undefined) cmd += ` to-addresses=${form.toAddresses || ''}`;
    if (form.toPorts !== undefined) cmd += ` to-ports=${form.toPorts || ''}`;
    if (form.comment !== undefined) cmd += ` comment="${escapeRouterOSValue(form.comment || '')}"`;
    return cmd;
  };

  // ===== Drag and Drop =====
  const handleDragStart = (e, index) => {
    dragItemRef.current = index;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    setTimeout(() => e.target.closest('tr')?.classList.add('dragging'), 0);
  };
  const handleDragOver = (e, index) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (index !== dragOverIndex) setDragOverIndex(index); };
  const handleDragLeave = () => setDragOverIndex(null);
  const handleDragEnd = (e) => { e.target.closest('tr')?.classList.remove('dragging'); setDragOverIndex(null); };

  const handleDrop = async (e, dropIndex, ruleType) => {
    e.preventDefault();
    const fromIndex = dragItemRef.current;
    setDragOverIndex(null);
    if (fromIndex === null || fromIndex === dropIndex) return;
    const rulesMap = { nat: natRules, addresslist: addressLists, mangle: mangleRules, queue: queues };
    const rules = rulesMap[ruleType] || filterRules;
    const draggedRule = rules[fromIndex];
    if (!draggedRule || !isValidRuleId(draggedRule.id)) { setError('Cannot move this rule'); return; }

    const pathMap = { nat: 'nat', addresslist: 'address-list', mangle: 'mangle', queue: 'queue simple' };
    const path = pathMap[ruleType] || 'filter';
    const prefix = ruleType === 'queue' ? '/ip' : '/ip firewall';

    setSaving(true); setError(null);
    try {
      const escapedId = escapeRouterOSValue(draggedRule.id);
      const command = `${prefix} ${path} move [find .id="${escapedId}"] destination=${dropIndex}`;
      const data = await executeCommand(command);
      if (data.success) {
        setSuccess(`Rule moved to position ${dropIndex}`);
        await loadFirewallRules();
      } else {
        setError(data.error || 'Failed to move rule');
      }
    } catch (err) {
        Sentry.captureException(err);
      setError(err.message || 'Failed to move rule');
    } finally {
      setSaving(false);
    }
  };

  // ===== Helpers =====
  const isGlobalRule = (rule) => {
    const c = rule.comment || '';
    return c.includes('CapTrac Template') || c.includes('CapTrac SNMP') || c.includes('CapTrac API') || c.includes('CapTrac Managed') || c.includes('CapTrac Placeholder');
  };

  const getRuleBadge = (rule) => {
    if (isGlobalRule(rule)) return <span className="rule-badge global-badge" title="Global / CapTrac Managed"><FaGlobe /> Global</span>;
    return <span className="rule-badge local-badge" title="Router-Specific"><FaServer /> Local</span>;
  };

  const getActionColor = (action) => {
    if (!action) return '#6c757d';
    const a = action.toLowerCase();
    if (a === 'accept') return '#4caf50';
    if (a === 'drop' || a === 'reject') return '#f44336';
    if (a === 'tarpit') return '#ff9800';
    if (a === 'masquerade' || a === 'src-nat') return '#9c27b0';
    if (a === 'dst-nat' || a === 'redirect' || a === 'netmap') return '#2196f3';
    if (a === 'passthrough') return '#607d8b';
    if (a.startsWith('mark-')) return '#795548';
    if (a.startsWith('change-')) return '#ff5722';
    return '#2196f3';
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // ===== Shared Render Pieces =====
  const renderDragHandle = (index, isDynamic) => (
    <td className="drag-handle-cell">
      {!isDynamic && (
        <span className="drag-handle" draggable onDragStart={(e) => handleDragStart(e, index)} title="Drag to reorder">
          <FaGripVertical />
        </span>
      )}
    </td>
  );

  const renderToggle = (rule, ruleType) => (
    <button onClick={() => toggleRuleState(rule, ruleType)} className="btn-icon btn-toggle" title={rule.disabled ? 'Enable' : 'Disable'} disabled={saving || rule.dynamic}>
      {rule.disabled ? <FaToggleOff color="#999" size={20} /> : <FaToggleOn color="#4caf50" size={20} />}
    </button>
  );

  const renderActionButtons = (rule, ruleType, canDelete = true) => (
    <div className="rule-actions">
      <button onClick={() => startEditingRule(rule)} className="btn-icon btn-edit" title="Edit" disabled={saving || rule.dynamic}><FaEdit /></button>
      {canDelete && <button onClick={() => deleteRule(rule, ruleType)} className="btn-icon btn-danger" title="Delete" disabled={saving || rule.dynamic || isGlobalRule(rule)}><FaTrash /></button>}
    </div>
  );

  const renderEditButtons = (ruleType) => (
    <div className="edit-actions">
      <button onClick={() => saveEditedRule(ruleType)} className="btn-save" disabled={saving} title="Save"><FaSave /></button>
      <button onClick={cancelEditing} className="btn-cancel" disabled={saving} title="Cancel"><FaTimes /></button>
    </div>
  );

  const dragRowProps = (index, ruleType) => ({
    onDragOver: (e) => handleDragOver(e, index),
    onDragLeave: handleDragLeave,
    onDrop: (e) => handleDrop(e, index, ruleType),
    onDragEnd: handleDragEnd
  });

  const rowClassName = (rule, index) => {
    const classes = ['rule-row'];
    if (rule.disabled) classes.push('disabled-rule');
    if (editingRuleId === rule.id) classes.push('editing');
    if (dragOverIndex === index) classes.push('drag-over');
    return classes.join(' ');
  };

  // ===== Tab Legend + Header =====
  const renderContentHeader = (ruleType, buttonLabel = 'Add Rule', showGlobalLegend = true) => (
    <div className="content-header">
      <div className="legend">
        {showGlobalLegend && (
          <>
            <span className="legend-item"><FaGlobe color="#2196f3" /> Global / Managed</span>
            <span className="legend-item"><FaServer color="#4caf50" /> Router-Specific</span>
          </>
        )}
        <span className="legend-item"><FaGripVertical color="#999" /> Drag to reorder</span>
      </div>
      <button onClick={() => openAddModal(ruleType)} className="btn-primary">
        <FaPlus /> {buttonLabel}
      </button>
    </div>
  );

  // ===== Render: Filter Rules =====
  const renderFilterRules = () => {
    if (filterRules.length === 0) return <div className="no-rules"><FaShieldAlt size={48} color="#ccc" /><p>No filter rules found</p><button onClick={() => openAddModal('filter')} className="btn-primary"><FaPlus /> Add First Rule</button></div>;
    return (
      <div className="rules-table-container">
        <table className="rules-table">
          <thead><tr>
            <th style={{ width: '30px' }}></th><th style={{ width: '40px' }}>#</th><th style={{ width: '80px' }}>Type</th><th style={{ width: '60px' }}>Status</th>
            <th>Chain</th><th>Action</th><th>Protocol</th><th>Src → Dst</th><th>Ports</th><th>Comment</th><th style={{ width: '120px' }}>Actions</th>
          </tr></thead>
          <tbody>
            {filterRules.map((rule, index) => {
              const isEditing = editingRuleId === rule.id;
              return (
                <tr key={rule.id || index} className={rowClassName(rule, index)} {...dragRowProps(index, 'filter')}>
                  {renderDragHandle(index, rule.dynamic)}
                  <td className="row-index">{index}</td>
                  <td>{getRuleBadge(rule)}</td>
                  <td>{renderToggle(rule, 'filter')}</td>
                  <td>{isEditing ? <select value={editForm.chain || ''} onChange={(e) => setEditForm({ ...editForm, chain: e.target.value })}><option value="input">input</option><option value="forward">forward</option><option value="output">output</option></select> : <span className="chain-badge">{rule.chain}</span>}</td>
                  <td>{isEditing ? <select value={editForm.action || ''} onChange={(e) => setEditForm({ ...editForm, action: e.target.value })}><option value="accept">accept</option><option value="drop">drop</option><option value="reject">reject</option><option value="tarpit">tarpit</option><option value="passthrough">passthrough</option></select> : <span className="action-badge" style={{ backgroundColor: getActionColor(rule.action) }}>{rule.action}</span>}</td>
                  <td>{isEditing ? <input type="text" value={editForm.protocol || ''} onChange={(e) => setEditForm({ ...editForm, protocol: e.target.value })} placeholder="any" /> : (rule.protocol || 'any')}</td>
                  <td>{isEditing ? <div><input type="text" value={editForm.srcAddress || ''} onChange={(e) => setEditForm({ ...editForm, srcAddress: e.target.value })} placeholder="Source IP" style={{ marginBottom: '4px' }} /><input type="text" value={editForm.srcAddressList || ''} onChange={(e) => setEditForm({ ...editForm, srcAddressList: e.target.value })} placeholder="Src Addr List" style={{ marginBottom: '4px' }} /><input type="text" value={editForm.dstAddress || ''} onChange={(e) => setEditForm({ ...editForm, dstAddress: e.target.value })} placeholder="Dest IP" style={{ marginBottom: '4px' }} /><input type="text" value={editForm.dstAddressList || ''} onChange={(e) => setEditForm({ ...editForm, dstAddressList: e.target.value })} placeholder="Dst Addr List" /></div> : <span className="address-info">{rule.srcAddress || rule.srcAddressList || 'any'} →<br />{rule.dstAddress || rule.dstAddressList || 'any'}</span>}</td>
                  <td>{isEditing ? <div><input type="text" value={editForm.srcPort || ''} onChange={(e) => setEditForm({ ...editForm, srcPort: e.target.value })} placeholder="Src" style={{ marginBottom: '4px' }} /><input type="text" value={editForm.dstPort || ''} onChange={(e) => setEditForm({ ...editForm, dstPort: e.target.value })} placeholder="Dst" /></div> : <span className="port-info">{rule.srcPort || 'any'} → {rule.dstPort || 'any'}</span>}</td>
                  <td>{isEditing ? <input type="text" value={editForm.comment || ''} onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })} placeholder="Comment" /> : <span className="comment-text" title={rule.comment}>{rule.comment || <em style={{ color: '#999' }}>-</em>}</span>}</td>
                  <td>{isEditing ? renderEditButtons('filter') : renderActionButtons(rule, 'filter')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ===== Render: NAT Rules =====
  const renderNatRules = () => {
    if (natRules.length === 0) return <div className="no-rules"><FaExchangeAlt size={48} color="#ccc" /><p>No NAT rules found</p><button onClick={() => openAddModal('nat')} className="btn-primary"><FaPlus /> Add NAT Rule</button></div>;
    return (
      <div className="rules-table-container">
        <table className="rules-table">
          <thead><tr>
            <th style={{ width: '30px' }}></th><th style={{ width: '40px' }}>#</th><th style={{ width: '80px' }}>Type</th><th style={{ width: '60px' }}>Status</th>
            <th>Chain</th><th>Action</th><th>Protocol</th><th>Src</th><th>Dst</th><th>To Addr</th><th>To Ports</th><th>Comment</th><th style={{ width: '120px' }}>Actions</th>
          </tr></thead>
          <tbody>
            {natRules.map((rule, index) => {
              const isEditing = editingRuleId === rule.id;
              return (
                <tr key={rule.id || index} className={rowClassName(rule, index)} {...dragRowProps(index, 'nat')}>
                  {renderDragHandle(index, rule.dynamic)}
                  <td className="row-index">{index}</td>
                  <td>{getRuleBadge(rule)}</td>
                  <td>{renderToggle(rule, 'nat')}</td>
                  <td>{isEditing ? <select value={editForm.chain || ''} onChange={(e) => setEditForm({ ...editForm, chain: e.target.value })}><option value="srcnat">srcnat</option><option value="dstnat">dstnat</option></select> : <span className="chain-badge">{rule.chain}</span>}</td>
                  <td>{isEditing ? <select value={editForm.action || ''} onChange={(e) => setEditForm({ ...editForm, action: e.target.value })}><option value="src-nat">src-nat</option><option value="dst-nat">dst-nat</option><option value="masquerade">masquerade</option><option value="netmap">netmap</option><option value="redirect">redirect</option><option value="accept">accept</option><option value="passthrough">passthrough</option></select> : <span className="action-badge" style={{ backgroundColor: getActionColor(rule.action) }}>{rule.action}</span>}</td>
                  <td>{isEditing ? <input type="text" value={editForm.protocol || ''} onChange={(e) => setEditForm({ ...editForm, protocol: e.target.value })} placeholder="any" /> : (rule.protocol || 'any')}</td>
                  <td>{isEditing ? <div><input type="text" value={editForm.srcAddress || ''} onChange={(e) => setEditForm({ ...editForm, srcAddress: e.target.value })} placeholder="IP/CIDR" style={{ marginBottom: '4px' }} /><input type="text" value={editForm.srcAddressList || ''} onChange={(e) => setEditForm({ ...editForm, srcAddressList: e.target.value })} placeholder="Src Addr List" /></div> : (rule.srcAddress || rule.srcAddressList ? <span className="address-info">{rule.srcAddress || ''}{rule.srcAddressList ? (rule.srcAddress ? <><br /><em>list: {rule.srcAddressList}</em></> : <em>{rule.srcAddressList}</em>) : ''}</span> : 'any')}</td>
                  <td>{isEditing ? <div><input type="text" value={editForm.dstAddress || ''} onChange={(e) => setEditForm({ ...editForm, dstAddress: e.target.value })} placeholder="IP/CIDR" style={{ marginBottom: '4px' }} /><input type="text" value={editForm.dstAddressList || ''} onChange={(e) => setEditForm({ ...editForm, dstAddressList: e.target.value })} placeholder="Dst Addr List" /></div> : (rule.dstAddress || rule.dstAddressList ? <span className="address-info">{rule.dstAddress || ''}{rule.dstAddressList ? (rule.dstAddress ? <><br /><em>list: {rule.dstAddressList}</em></> : <em>{rule.dstAddressList}</em>) : ''}</span> : 'any')}</td>
                  <td>{isEditing ? <input type="text" value={editForm.toAddresses || ''} onChange={(e) => setEditForm({ ...editForm, toAddresses: e.target.value })} placeholder="-" /> : (rule.toAddresses || '-')}</td>
                  <td>{isEditing ? <input type="text" value={editForm.toPorts || ''} onChange={(e) => setEditForm({ ...editForm, toPorts: e.target.value })} placeholder="-" /> : (rule.toPorts || '-')}</td>
                  <td>{isEditing ? <input type="text" value={editForm.comment || ''} onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })} /> : <span className="comment-text" title={rule.comment}>{rule.comment || '-'}</span>}</td>
                  <td>{isEditing ? renderEditButtons('nat') : renderActionButtons(rule, 'nat')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ===== Render: Address Lists =====
  const renderAddressLists = () => {
    if (addressLists.length === 0) return <div className="no-rules"><FaListUl size={48} color="#ccc" /><p>No address list entries found</p><button onClick={() => openAddModal('addresslist')} className="btn-primary"><FaPlus /> Add Entry</button></div>;
    return (
      <div className="rules-table-container">
        <table className="rules-table">
          <thead><tr>
            <th style={{ width: '30px' }}></th><th style={{ width: '40px' }}>#</th><th style={{ width: '80px' }}>Type</th><th style={{ width: '60px' }}>Status</th>
            <th>List</th><th>Address</th><th>Timeout</th><th>Creation Time</th><th>Comment</th><th style={{ width: '120px' }}>Actions</th>
          </tr></thead>
          <tbody>
            {addressLists.map((entry, index) => {
              const isEditing = editingRuleId === entry.id;
              return (
                <tr key={entry.id || index} className={rowClassName(entry, index)} {...dragRowProps(index, 'addresslist')}>
                  {renderDragHandle(index, entry.dynamic)}
                  <td className="row-index">{index}</td>
                  <td>{getRuleBadge(entry)}</td>
                  <td>{renderToggle(entry, 'addresslist')}</td>
                  <td>{isEditing ? <input type="text" value={editForm.list || ''} onChange={(e) => setEditForm({ ...editForm, list: e.target.value })} /> : <strong>{sanitizeText(entry.list)}</strong>}</td>
                  <td>{isEditing ? <input type="text" value={editForm.address || ''} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} /> : <span className="address-info">{sanitizeText(entry.address)}</span>}</td>
                  <td>{isEditing ? <input type="text" value={editForm.timeout || ''} onChange={(e) => setEditForm({ ...editForm, timeout: e.target.value })} placeholder="permanent" /> : (entry.timeout || <em style={{ color: '#999' }}>permanent</em>)}</td>
                  <td>{entry.creationTime || '-'}</td>
                  <td>{isEditing ? <input type="text" value={editForm.comment || ''} onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })} /> : <span className="comment-text" title={entry.comment}>{entry.comment || '-'}</span>}</td>
                  <td>{isEditing ? renderEditButtons('addresslist') : renderActionButtons(entry, 'addresslist')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ===== Render: Mangle Rules =====
  const renderMangleRules = () => {
    if (mangleRules.length === 0) return <div className="no-rules"><FaLayerGroup size={48} color="#ccc" /><p>No mangle rules found</p><button onClick={() => openAddModal('mangle')} className="btn-primary"><FaPlus /> Add Mangle Rule</button></div>;
    return (
      <div className="rules-table-container">
        <table className="rules-table">
          <thead><tr>
            <th style={{ width: '30px' }}></th><th style={{ width: '40px' }}>#</th><th style={{ width: '80px' }}>Type</th><th style={{ width: '60px' }}>Status</th>
            <th>Chain</th><th>Action</th><th>Protocol</th><th>Src → Dst</th><th>Marks</th><th>Bytes</th><th>Comment</th><th style={{ width: '120px' }}>Actions</th>
          </tr></thead>
          <tbody>
            {mangleRules.map((rule, index) => {
              const isEditing = editingRuleId === rule.id;
              const marks = [rule.newConnectionMark, rule.newPacketMark, rule.newRoutingMark].filter(Boolean).join(', ') || '-';
              return (
                <tr key={rule.id || index} className={rowClassName(rule, index)} {...dragRowProps(index, 'mangle')}>
                  {renderDragHandle(index, rule.dynamic)}
                  <td className="row-index">{index}</td>
                  <td>{getRuleBadge(rule)}</td>
                  <td>{renderToggle(rule, 'mangle')}</td>
                  <td>{isEditing ? <select value={editForm.chain || ''} onChange={(e) => setEditForm({ ...editForm, chain: e.target.value })}><option value="prerouting">prerouting</option><option value="input">input</option><option value="forward">forward</option><option value="output">output</option><option value="postrouting">postrouting</option></select> : <span className="chain-badge">{rule.chain}</span>}</td>
                  <td>{isEditing ? <select value={editForm.action || ''} onChange={(e) => setEditForm({ ...editForm, action: e.target.value })}><option value="mark-connection">mark-connection</option><option value="mark-packet">mark-packet</option><option value="mark-routing">mark-routing</option><option value="accept">accept</option><option value="passthrough">passthrough</option><option value="change-mss">change-mss</option><option value="change-dscp">change-dscp</option></select> : <span className="action-badge" style={{ backgroundColor: getActionColor(rule.action) }}>{rule.action}</span>}</td>
                  <td>{rule.protocol || 'any'}</td>
                  <td><span className="address-info">{rule.srcAddress || 'any'} →<br />{rule.dstAddress || 'any'}</span></td>
                  <td><span className="mark-info">{marks}</span></td>
                  <td>{formatBytes(rule.bytes || 0)}</td>
                  <td><span className="comment-text" title={rule.comment}>{rule.comment || '-'}</span></td>
                  <td>{isEditing ? renderEditButtons('mangle') : renderActionButtons(rule, 'mangle')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ===== Render: Queues =====
  const renderQueues = () => {
    if (queues.length === 0) return <div className="no-rules"><FaTachometerAlt size={48} color="#ccc" /><p>No queues found</p><button onClick={() => openAddModal('queue')} className="btn-primary"><FaPlus /> Add Queue</button></div>;
    return (
      <div className="rules-table-container">
        <table className="rules-table">
          <thead><tr>
            <th style={{ width: '30px' }}></th><th style={{ width: '40px' }}>#</th><th style={{ width: '80px' }}>Type</th><th style={{ width: '60px' }}>Status</th>
            <th>Name</th><th>Target</th><th>Max Limit</th><th>Burst</th><th>Priority</th><th>Bytes</th><th>Comment</th><th style={{ width: '120px' }}>Actions</th>
          </tr></thead>
          <tbody>
            {queues.map((q, index) => {
              const isEditing = editingRuleId === q.id;
              return (
                <tr key={q.id || index} className={rowClassName(q, index)} {...dragRowProps(index, 'queue')}>
                  {renderDragHandle(index, q.dynamic)}
                  <td className="row-index">{index}</td>
                  <td>{getRuleBadge(q)}</td>
                  <td>{renderToggle(q, 'queue')}</td>
                  <td>{isEditing ? <input type="text" value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /> : <strong>{sanitizeText(q.name)}</strong>}</td>
                  <td>{isEditing ? <input type="text" value={editForm.target || ''} onChange={(e) => setEditForm({ ...editForm, target: e.target.value })} /> : <span className="address-info">{q.target}</span>}</td>
                  <td>{isEditing ? <input type="text" value={editForm.maxLimit || ''} onChange={(e) => setEditForm({ ...editForm, maxLimit: e.target.value })} placeholder="0/0" /> : (q.maxLimit || '0/0')}</td>
                  <td>{q.burstLimit || '-'}</td>
                  <td>{q.priority || '8'}</td>
                  <td>{formatBytes(q.bytes || 0)}</td>
                  <td>{isEditing ? <input type="text" value={editForm.comment || ''} onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })} /> : <span className="comment-text" title={q.comment}>{q.comment || '-'}</span>}</td>
                  <td>{isEditing ? renderEditButtons('queue') : renderActionButtons(q, 'queue')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ===== Render: Routes =====
  const renderRoutes = () => {
    if (routes.length === 0) return <div className="no-rules"><FaRoute size={48} color="#ccc" /><p>No static routes found</p><button onClick={() => openAddModal('route')} className="btn-primary"><FaPlus /> Add Route</button></div>;
    return (
      <div className="rules-table-container">
        <table className="rules-table">
          <thead><tr>
            <th style={{ width: '40px' }}>#</th><th style={{ width: '80px' }}>Type</th><th style={{ width: '60px' }}>Status</th>
            <th>Destination</th><th>Gateway</th><th>Distance</th><th>Active</th><th>Routing Mark</th><th>Comment</th><th style={{ width: '120px' }}>Actions</th>
          </tr></thead>
          <tbody>
            {routes.map((r, index) => {
              const isEditing = editingRuleId === r.id;
              return (
                <tr key={r.id || index} className={rowClassName(r, index)}>
                  <td className="row-index">{index}</td>
                  <td>{getRuleBadge(r)}</td>
                  <td>{renderToggle(r, 'route')}</td>
                  <td>{isEditing ? <input type="text" value={editForm.dstAddress || ''} onChange={(e) => setEditForm({ ...editForm, dstAddress: e.target.value })} /> : <span className="address-info">{r.dstAddress}</span>}</td>
                  <td>{isEditing ? <input type="text" value={editForm.gateway || ''} onChange={(e) => setEditForm({ ...editForm, gateway: e.target.value })} /> : r.gateway}</td>
                  <td>{isEditing ? <input type="text" value={editForm.distance || ''} onChange={(e) => setEditForm({ ...editForm, distance: e.target.value })} /> : r.distance}</td>
                  <td>{r.active ? <span className="action-badge" style={{ backgroundColor: '#4caf50' }}>active</span> : <span className="action-badge" style={{ backgroundColor: '#6c757d' }}>inactive</span>}</td>
                  <td>{r.routingMark || '-'}</td>
                  <td>{isEditing ? <input type="text" value={editForm.comment || ''} onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })} /> : <span className="comment-text" title={r.comment}>{r.comment || '-'}</span>}</td>
                  <td>{isEditing ? renderEditButtons('route') : renderActionButtons(r, 'route', !r.dynamic)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ===== Render: DNS =====
  const renderDns = () => {
    if (dnsEntries.length === 0) return <div className="no-rules"><FaNetworkWired size={48} color="#ccc" /><p>No static DNS entries found</p><button onClick={() => openAddModal('dns')} className="btn-primary"><FaPlus /> Add DNS Entry</button></div>;
    return (
      <div className="rules-table-container">
        <table className="rules-table">
          <thead><tr>
            <th style={{ width: '40px' }}>#</th><th style={{ width: '80px' }}>Type</th><th style={{ width: '60px' }}>Status</th>
            <th>Name</th><th>Address</th><th>TTL</th><th>Comment</th><th style={{ width: '120px' }}>Actions</th>
          </tr></thead>
          <tbody>
            {dnsEntries.map((d, index) => {
              const isEditing = editingRuleId === d.id;
              return (
                <tr key={d.id || index} className={rowClassName(d, index)}>
                  <td className="row-index">{index}</td>
                  <td>{getRuleBadge(d)}</td>
                  <td>{renderToggle(d, 'dns')}</td>
                  <td>{isEditing ? <input type="text" value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /> : <strong>{sanitizeText(d.name)}</strong>}</td>
                  <td>{isEditing ? <input type="text" value={editForm.address || ''} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} /> : <span className="address-info">{d.address}</span>}</td>
                  <td>{d.ttl || '-'}</td>
                  <td>{isEditing ? <input type="text" value={editForm.comment || ''} onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })} /> : <span className="comment-text" title={d.comment}>{d.comment || '-'}</span>}</td>
                  <td>{isEditing ? renderEditButtons('dns') : renderActionButtons(d, 'dns')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ===== Render: DHCP Leases =====
  const renderDhcp = () => {
    if (dhcpLeases.length === 0) return <div className="no-rules"><FaDesktop size={48} color="#ccc" /><p>No DHCP leases found</p><button onClick={() => openAddModal('dhcp')} className="btn-primary"><FaPlus /> Add Static Lease</button></div>;
    return (
      <div className="rules-table-container">
        <table className="rules-table">
          <thead><tr>
            <th style={{ width: '40px' }}>#</th><th style={{ width: '80px' }}>Type</th><th style={{ width: '60px' }}>Status</th>
            <th>Address</th><th>MAC Address</th><th>Host Name</th><th>Server</th><th>Lease Status</th><th>Expires After</th><th>Comment</th><th style={{ width: '120px' }}>Actions</th>
          </tr></thead>
          <tbody>
            {dhcpLeases.map((l, index) => {
              const isEditing = editingRuleId === l.id;
              const statusColor = l.status === 'bound' ? '#4caf50' : l.status === 'waiting' ? '#ff9800' : '#6c757d';
              return (
                <tr key={l.id || index} className={rowClassName(l, index)}>
                  <td className="row-index">{index}</td>
                  <td>{getRuleBadge(l)}</td>
                  <td>{renderToggle(l, 'dhcp')}</td>
                  <td>{isEditing ? <input type="text" value={editForm.address || ''} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} /> : <span className="address-info">{l.address}</span>}</td>
                  <td>{isEditing ? <input type="text" value={editForm.macAddress || ''} onChange={(e) => setEditForm({ ...editForm, macAddress: e.target.value })} /> : <span className="address-info">{l.macAddress}</span>}</td>
                  <td>{l.hostName || '-'}</td>
                  <td>{l.server || '-'}</td>
                  <td>{l.status ? <span className="action-badge" style={{ backgroundColor: statusColor }}>{l.status}</span> : '-'}</td>
                  <td>{l.expiresAfter || <em style={{ color: '#999' }}>static</em>}</td>
                  <td>{isEditing ? <input type="text" value={editForm.comment || ''} onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })} /> : <span className="comment-text" title={l.comment}>{l.comment || '-'}</span>}</td>
                  <td>{isEditing ? renderEditButtons('dhcp') : renderActionButtons(l, 'dhcp', !l.dynamic)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ===== Render: L7 Protocols =====
  /**
   * Renders the Layer 7 protocol definitions table.
   *
   * Each L7 entry pairs a human-readable name with a POSIX regex pattern used
   * by MikroTik to identify the protocol from the connection's first payloads.
   * Entries can be globally managed (comment prefixed with "CapTrac L7") or
   * created locally on this router.
   */
  const renderL7Protocols = () => {
    if (l7Protocols.length === 0) {
      return (
        <div className="no-rules">
          <FaCode size={48} color="#ccc" />
          <p>No Layer 7 protocols defined on this router</p>
          <button onClick={() => openAddModal('l7')} className="btn-primary">
            <FaPlus /> Add L7 Protocol
          </button>
        </div>
      );
    }

    return (
      <div className="rules-table-container">
        <table className="rules-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>#</th>
              <th style={{ width: '80px' }}>Type</th>
              <th>Name</th>
              <th>Regex Pattern</th>
              <th>Comment</th>
              <th style={{ width: '120px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {l7Protocols.map((proto, index) => {
              const isEditing = editingRuleId === proto.id;
              return (
                <tr key={proto.id || index} className={rowClassName(proto, index)}>
                  <td className="row-index">{index}</td>
                  <td>{getRuleBadge(proto)}</td>
                  <td>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.name || ''}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        placeholder="Protocol name"
                      />
                    ) : (
                      <strong>{sanitizeText(proto.name)}</strong>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.regexp || ''}
                        onChange={(e) => setEditForm({ ...editForm, regexp: e.target.value })}
                        placeholder="POSIX regex pattern"
                        style={{ fontFamily: 'monospace', fontSize: '12px', width: '100%' }}
                      />
                    ) : (
                      <code className="l7-regex-preview" title={proto.regexp}>
                        {proto.regexp && proto.regexp.length > 60
                          ? `${proto.regexp.slice(0, 60)}…`
                          : (proto.regexp || '—')}
                      </code>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.comment || ''}
                        onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })}
                        placeholder="Comment"
                      />
                    ) : (
                      <span className="comment-text" title={proto.comment}>
                        {proto.comment || <em style={{ color: '#999' }}>—</em>}
                      </span>
                    )}
                  </td>
                  <td>
                    {isEditing
                      ? renderEditButtons('l7')
                      : renderActionButtons(proto, 'l7', !proto.dynamic)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ===== Render: Add Modal =====
  const renderAddModal = () => {
    const t = addModalType;
    const titles = { filter: 'Add Filter Rule', nat: 'Add NAT Rule', addresslist: 'Add Address List Entry', mangle: 'Add Mangle Rule', queue: 'Add Simple Queue', route: 'Add Static Route', dns: 'Add DNS Entry', dhcp: 'Add DHCP Lease', l7: 'Add L7 Protocol' };
    const f = newRuleForm;
    const u = (key, val) => setNewRuleForm({ ...f, [key]: val });

    return (
      <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3><FaPlus /> {titles[t]}</h3>
            <button onClick={() => setShowAddModal(false)} className="btn-close"><FaTimes /></button>
          </div>
          <div className="modal-body">
            <div className="form-grid">
              {t === 'addresslist' && (<>
                <div className="form-group"><label>List Name *</label>
                  <select value={f.list || ''} onChange={(e) => u('list', e.target.value)}>
                    <option value="">-- Select list --</option>
                    {[...new Set(addressLists.map(e => e.list).filter(Boolean))].sort().map(name => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                    <option value="__custom__">Other (type manually)...</option>
                  </select>
                </div>
                {f.list === '__custom__' && (
                  <div className="form-group"><label>Custom List Name *</label><input type="text" value={f.customList || ''} onChange={(e) => u('customList', e.target.value)} placeholder="e.g., MyNewList" /></div>
                )}
                <div className="form-group"><label>Address *</label><input type="text" value={f.address || ''} onChange={(e) => u('address', e.target.value)} placeholder="IP or CIDR" /></div>
                <div className="form-group"><label>Timeout</label><input type="text" value={f.timeout || ''} onChange={(e) => u('timeout', e.target.value)} placeholder="empty = permanent" /></div>
                <div className="form-group full-width"><label>Comment</label><input type="text" value={f.comment || ''} onChange={(e) => u('comment', e.target.value)} /></div>
              </>)}
              {t === 'queue' && (<>
                <div className="form-group"><label>Name *</label><input type="text" value={f.name || ''} onChange={(e) => u('name', e.target.value)} placeholder="Queue name" /></div>
                <div className="form-group"><label>Target *</label><input type="text" value={f.target || ''} onChange={(e) => u('target', e.target.value)} placeholder="IP or subnet" /></div>
                <div className="form-group"><label>Max Limit (up/down)</label><input type="text" value={f.maxLimit || ''} onChange={(e) => u('maxLimit', e.target.value)} placeholder="e.g., 10M/10M" /></div>
                <div className="form-group"><label>Burst Limit</label><input type="text" value={f.burstLimit || ''} onChange={(e) => u('burstLimit', e.target.value)} placeholder="e.g., 15M/15M" /></div>
                <div className="form-group"><label>Burst Threshold</label><input type="text" value={f.burstThreshold || ''} onChange={(e) => u('burstThreshold', e.target.value)} placeholder="e.g., 8M/8M" /></div>
                <div className="form-group"><label>Burst Time</label><input type="text" value={f.burstTime || ''} onChange={(e) => u('burstTime', e.target.value)} placeholder="e.g., 10/10" /></div>
                <div className="form-group"><label>Priority (1-8)</label><input type="text" value={f.priority || '8'} onChange={(e) => u('priority', e.target.value)} /></div>
                <div className="form-group full-width"><label>Comment</label><input type="text" value={f.comment || ''} onChange={(e) => u('comment', e.target.value)} /></div>
              </>)}
              {t === 'route' && (<>
                <div className="form-group"><label>Destination *</label><input type="text" value={f.dstAddress || '0.0.0.0/0'} onChange={(e) => u('dstAddress', e.target.value)} /></div>
                <div className="form-group"><label>Gateway *</label><input type="text" value={f.gateway || ''} onChange={(e) => u('gateway', e.target.value)} placeholder="IP or interface" /></div>
                <div className="form-group"><label>Distance</label><input type="text" value={f.distance || '1'} onChange={(e) => u('distance', e.target.value)} /></div>
                <div className="form-group"><label>Routing Mark</label><input type="text" value={f.routingMark || ''} onChange={(e) => u('routingMark', e.target.value)} /></div>
                <div className="form-group full-width"><label>Comment</label><input type="text" value={f.comment || ''} onChange={(e) => u('comment', e.target.value)} /></div>
              </>)}
              {t === 'dns' && (<>
                <div className="form-group"><label>Name *</label><input type="text" value={f.name || ''} onChange={(e) => u('name', e.target.value)} placeholder="e.g., myhost.local" /></div>
                <div className="form-group"><label>Address *</label><input type="text" value={f.address || ''} onChange={(e) => u('address', e.target.value)} placeholder="IP address" /></div>
                <div className="form-group"><label>TTL</label><input type="text" value={f.ttl || '1d'} onChange={(e) => u('ttl', e.target.value)} /></div>
                <div className="form-group full-width"><label>Comment</label><input type="text" value={f.comment || ''} onChange={(e) => u('comment', e.target.value)} /></div>
              </>)}
              {t === 'dhcp' && (<>
                <div className="form-group"><label>IP Address *</label><input type="text" value={f.address || ''} onChange={(e) => u('address', e.target.value)} placeholder="e.g., 192.168.1.100" /></div>
                <div className="form-group"><label>MAC Address *</label><input type="text" value={f.macAddress || ''} onChange={(e) => u('macAddress', e.target.value)} placeholder="AA:BB:CC:DD:EE:FF" /></div>
                <div className="form-group"><label>Server</label><input type="text" value={f.server || 'defconf'} onChange={(e) => u('server', e.target.value)} /></div>
                <div className="form-group full-width"><label>Comment</label><input type="text" value={f.comment || ''} onChange={(e) => u('comment', e.target.value)} /></div>
              </>)}
              {t === 'l7' && (<>
                <div className="form-group">
                  <label>Protocol Name *</label>
                  <input
                    type="text"
                    value={f.name || ''}
                    onChange={(e) => u('name', e.target.value)}
                    placeholder="e.g., whatsapp"
                  />
                  <small style={{ color: '#6c757d', marginTop: '4px', display: 'block' }}>
                    Used as the MikroTik layer7-protocol name. No spaces (they are replaced with hyphens).
                  </small>
                </div>
                <div className="form-group">
                  <label>Comment</label>
                  <input
                    type="text"
                    value={f.comment || ''}
                    onChange={(e) => u('comment', e.target.value)}
                    placeholder="Optional note about this protocol"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Regex Pattern *</label>
                  <textarea
                    rows={4}
                    value={f.regexp || ''}
                    onChange={(e) => u('regexp', e.target.value)}
                    placeholder="e.g., whatsapp|wa\\.me|whatsapp\\.com|mmg\\.whatsapp\\.net"
                    style={{
                      width: '100%',
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      padding: '8px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      resize: 'vertical'
                    }}
                  />
                  <small style={{ color: '#6c757d', marginTop: '4px', display: 'block' }}>
                    POSIX-compatible regex. MikroTik matches this against the first 10 packets
                    of each connection&apos;s payload (application layer).
                  </small>
                </div>
              </>)}
              {(t === 'filter' || t === 'nat' || t === 'mangle') && (<>
                <div className="form-group"><label>Chain *</label>
                  <select value={f.chain} onChange={(e) => u('chain', e.target.value)}>
                    {t === 'filter' && <><option value="input">Input</option><option value="forward">Forward</option><option value="output">Output</option></>}
                    {t === 'nat' && <><option value="dstnat">dstnat</option><option value="srcnat">srcnat</option></>}
                    {t === 'mangle' && <><option value="prerouting">prerouting</option><option value="input">input</option><option value="forward">forward</option><option value="output">output</option><option value="postrouting">postrouting</option></>}
                  </select>
                </div>
                <div className="form-group"><label>Action *</label>
                  <select value={f.action} onChange={(e) => u('action', e.target.value)}>
                    {t === 'filter' && <><option value="accept">accept</option><option value="drop">drop</option><option value="reject">reject</option><option value="tarpit">tarpit</option><option value="passthrough">passthrough</option></>}
                    {t === 'nat' && <><option value="dst-nat">dst-nat</option><option value="src-nat">src-nat</option><option value="masquerade">masquerade</option><option value="netmap">netmap</option><option value="redirect">redirect</option><option value="accept">accept</option></>}
                    {t === 'mangle' && <><option value="mark-connection">mark-connection</option><option value="mark-packet">mark-packet</option><option value="mark-routing">mark-routing</option><option value="accept">accept</option><option value="passthrough">passthrough</option><option value="change-mss">change-mss</option><option value="change-dscp">change-dscp</option></>}
                  </select>
                </div>
                <div className="form-group"><label>Protocol</label><select value={f.protocol || ''} onChange={(e) => u('protocol', e.target.value)}><option value="">Any</option><option value="tcp">TCP</option><option value="udp">UDP</option><option value="icmp">ICMP</option><option value="gre">GRE</option></select></div>
                {t === 'filter' && <div className="form-group"><label>Connection State</label><input type="text" value={f.connectionState || ''} onChange={(e) => u('connectionState', e.target.value)} placeholder="established,related" /></div>}
                <div className="form-group"><label>Source Address</label><input type="text" value={f.srcAddress || ''} onChange={(e) => u('srcAddress', e.target.value)} placeholder="IP or CIDR" /></div>
                <div className="form-group"><label>Src Address List</label><input type="text" value={f.srcAddressList || ''} onChange={(e) => u('srcAddressList', e.target.value)} placeholder="e.g., Whitelist or !Blacklist" /></div>
                <div className="form-group"><label>Destination Address</label><input type="text" value={f.dstAddress || ''} onChange={(e) => u('dstAddress', e.target.value)} placeholder="IP or CIDR" /></div>
                <div className="form-group"><label>Dst Address List</label><input type="text" value={f.dstAddressList || ''} onChange={(e) => u('dstAddressList', e.target.value)} placeholder="e.g., Whitelist or !Blacklist" /></div>
                <div className="form-group"><label>Source Port</label><input type="text" value={f.srcPort || ''} onChange={(e) => u('srcPort', e.target.value)} placeholder="e.g., 80" /></div>
                <div className="form-group"><label>Destination Port</label><input type="text" value={f.dstPort || ''} onChange={(e) => u('dstPort', e.target.value)} placeholder="e.g., 443" /></div>
                {t === 'nat' && <><div className="form-group"><label>To Addresses</label><input type="text" value={f.toAddresses || ''} onChange={(e) => u('toAddresses', e.target.value)} /></div><div className="form-group"><label>To Ports</label><input type="text" value={f.toPorts || ''} onChange={(e) => u('toPorts', e.target.value)} /></div></>}
                {t === 'mangle' && <><div className="form-group"><label>New Conn. Mark</label><input type="text" value={f.newConnectionMark || ''} onChange={(e) => u('newConnectionMark', e.target.value)} /></div><div className="form-group"><label>New Packet Mark</label><input type="text" value={f.newPacketMark || ''} onChange={(e) => u('newPacketMark', e.target.value)} /></div><div className="form-group"><label>New Routing Mark</label><input type="text" value={f.newRoutingMark || ''} onChange={(e) => u('newRoutingMark', e.target.value)} /></div></>}
                <div className="form-group full-width"><label>Comment</label><input type="text" value={f.comment || ''} onChange={(e) => u('comment', e.target.value)} /></div>
                <div className="form-group"><label><input type="checkbox" checked={f.log || false} onChange={(e) => u('log', e.target.checked)} /> Enable Logging</label></div>
              </>)}
            </div>
          </div>
          <div className="modal-footer">
            <button onClick={() => setShowAddModal(false)} className="btn-secondary">Cancel</button>
            <button onClick={addNewRule} className="btn-primary" disabled={saving}>{saving ? 'Adding...' : 'Add'}</button>
          </div>
        </div>
      </div>
    );
  };

  // ===== Tab Config =====
  const tabs = [
    { key: 'filter', label: 'Filter Rules', icon: <FaShieldAlt />, count: filterRules.length },
    { key: 'nat', label: 'NAT Rules', icon: <FaExchangeAlt />, count: natRules.length },
    { key: 'lists', label: 'Address Lists', icon: <FaListUl />, count: addressLists.length },
    { key: 'mangle', label: 'Mangle', icon: <FaLayerGroup />, count: mangleRules.length },
    { key: 'queues', label: 'Queues', icon: <FaTachometerAlt />, count: queues.length },
    { key: 'routes', label: 'Routes', icon: <FaRoute />, count: routes.length },
    { key: 'dns', label: 'DNS', icon: <FaNetworkWired />, count: dnsEntries.length },
    { key: 'dhcp', label: 'DHCP', icon: <FaDesktop />, count: dhcpLeases.length },
    { key: 'l7', label: 'L7 Rules', icon: <FaCode />, count: l7Protocols.length },
  ];

  const tabRender = {
    filter: { header: () => renderContentHeader('filter', 'Add Rule'), body: renderFilterRules },
    nat: { header: () => renderContentHeader('nat', 'Add NAT Rule'), body: renderNatRules },
    lists: { header: () => renderContentHeader('addresslist', 'Add Entry'), body: renderAddressLists },
    mangle: { header: () => renderContentHeader('mangle', 'Add Mangle Rule'), body: renderMangleRules },
    queues: { header: () => renderContentHeader('queue', 'Add Queue'), body: renderQueues },
    routes: { header: () => renderContentHeader('route', 'Add Route', false), body: renderRoutes },
    dns: { header: () => renderContentHeader('dns', 'Add DNS Entry', false), body: renderDns },
    dhcp: { header: () => renderContentHeader('dhcp', 'Add Lease', false), body: renderDhcp },
    l7: { header: () => renderContentHeader('l7', 'Add L7 Protocol'), body: renderL7Protocols },
  };

  // ===== Main Render =====
  return (
    <div className="router-firewall-editor">
      <div className="firewall-header">
        <div className="header-title">
          <FaShieldAlt size={24} />
          <h2>Router Management</h2>
        </div>
        <div className="mode-toggle">
          <button className={`mode-btn ${mode === 'single' ? 'active' : ''}`} onClick={() => setMode('single')}><FaServer /> Configure This Router</button>
          <button className={`mode-btn ${mode === 'multiple' ? 'active' : ''}`} onClick={() => setMode('multiple')}><FaGlobe /> Deploy to Multiple</button>
        </div>
        <button onClick={loadFirewallRules} className="btn-refresh" disabled={loading} title="Refresh"><FaSync className={loading ? 'spinning' : ''} /> Refresh</button>
      </div>

      {error && <div className="alert alert-error"><FaExclamationTriangle /> {sanitizeText(error)}<button onClick={() => setError(null)} className="alert-close">×</button></div>}
      {success && <div className="alert alert-success"><FaCheckCircle /> {sanitizeText(success)}<button onClick={() => setSuccess(null)} className="alert-close">×</button></div>}

      {mode === 'single' && (
        <div className="single-router-mode">
          <div className="router-info">
            <strong>{sanitizeText(routerName)}</strong>
            {lastFetched && <span className="fetch-time"><FaClock /> Last fetched: {new Date(lastFetched).toLocaleString()}</span>}
          </div>

          <div className="firewall-tabs">
            {tabs.map(tab => (
              <button key={tab.key} className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`} onClick={() => { setActiveTab(tab.key); cancelEditing(); }}>
                {tab.icon} {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          <div className="tab-content">
            {tabRender[activeTab] && (
              <>
                {tabRender[activeTab].header()}
                {loading ? <div className="loading-state"><div className="spinner"></div><p>Loading...</p></div> : tabRender[activeTab].body()}
              </>
            )}
          </div>
        </div>
      )}

      {mode === 'multiple' && (
        <div className="multiple-router-mode">
          <MultiRouterDeployment apiFetch={apiFetch} />
        </div>
      )}

      {showAddModal && renderAddModal()}
    </div>
  );
};

export default RouterFirewallEditor;
